import { pipe } from 'fp-ts/function';
import { array, nonEmptyArray } from 'fp-ts';
import { Product, ProductCategory, Rate, RateResult, RateType } from '@mews-commander/data-access';
import { TextKey } from '@mews-commander/enums';
import { NestedOptions, Option, TreeOption } from '@mews/b2b-ui';

export const createRateGroupOptions = ({
    serviceId,
    rates: { rates, rateGroups },
    localizeNames,
    localizeText,
    deletedRatesCategory,
}: {
    serviceId: string;
    rates: RateResult;
    localizeNames: (names: { [key: string]: string }) => string;
    localizeText: ({ textKey }: { textKey: Mews.TextKey }) => string;
    deletedRatesCategory: TreeOption | null;
}) => {
    if (!rates || !rateGroups) return [];

    const existingRateGroupOptions = pipe(
        rates,
        array.filter((rate) => rate.serviceId === serviceId),
        nonEmptyArray.groupBy((rate) => rate.rateGroupId),
        Object.entries<Rate[]>,
        array.map(([rateGroupId, rateGroupRates]) => {
            const publicRates = rateGroupRates.filter((rate) => rate.type === RateType.Public);
            const privateRates = rateGroupRates.filter((rate) => rate.type === RateType.Private);

            const publicRateGroupOptions = {
                id: 'publicRates',
                label: localizeText({ textKey: TextKey.RoutingRulesFormRatesPublicRatesOption }),
                options: publicRates.map((rate: Rate) => ({
                    id: rate.id,
                    value: rate.id,
                    label: rate.name,
                })),
            };

            const privateRateGroupOptions = {
                id: 'privateRates',
                label: localizeText({ textKey: TextKey.RoutingRulesFormRatesPrivateRatesOption }),
                options: privateRates.map((rate: Rate) => ({
                    id: rate.id,
                    value: rate.id,
                    label: rate.name,
                })),
            };

            const rateGroupOptions = [
                ...(publicRates.length > 0 ? [publicRateGroupOptions] : []),
                ...(privateRates.length > 0 ? [privateRateGroupOptions] : []),
            ];

            return {
                id: rateGroupId,
                label: localizeNames(rateGroups.find((group) => group.id === rateGroupId)?.names || {}),
                options: rateGroupOptions,
            };
        })
    );

    return deletedRatesCategory != null
        ? [...existingRateGroupOptions, deletedRatesCategory]
        : [...existingRateGroupOptions];
};

const removeEmptyOptions = (options: (Option | NestedOptions)[]): (Option | NestedOptions)[] =>
    options.filter((option) => {
        if ('options' in option) {
            option.options = removeEmptyOptions(option.options);
            return option.options.length > 0;
        }
        return true;
    });

export const createProductGroupOptions = ({
    products,
    productCategories,
    localizeNames,
    deletedProductsCategory,
}: {
    products: Product[];
    productCategories: ProductCategory[];
    localizeNames: (names: { [key: string]: string }) => string;
    deletedProductsCategory: TreeOption | null;
}) => {
    // Build a map of categories to their subcategories and products
    const categoryMap: { [key: string]: NestedOptions } = {};

    productCategories.forEach((category) => {
        categoryMap[category.id] = {
            label: localizeNames(category.names),
            id: category.id,
            options: [],
        };
    });

    productCategories.forEach((category) => {
        if (category.parentCategoryId && categoryMap[category.parentCategoryId]) {
            categoryMap[category.parentCategoryId].options.push(categoryMap[category.id]);
        }
    });

    products.forEach((product) => {
        const productOption = {
            label: localizeNames(product.names),
            id: product.id,
            value: product.id,
        };

        if (product.productCategoryId && categoryMap[product.productCategoryId]) {
            categoryMap[product.productCategoryId].options.push(productOption);
        }
    });

    const topLevelCategories = productCategories
        .filter((category) => !category.parentCategoryId)
        .map((category) => categoryMap[category.id]);

    const productsWithoutCategories = pipe(
        products,
        array.filter((product) => !product.productCategoryId),
        array.map((product) => ({
            label: localizeNames(product.names),
            id: product.id,
            value: product.id,
        }))
    );

    const finalCategories = removeEmptyOptions(topLevelCategories);

    return deletedProductsCategory != null
        ? [...finalCategories, ...productsWithoutCategories, deletedProductsCategory]
        : [...finalCategories, ...productsWithoutCategories];
};
